import type { AccessSurface } from '@/api/accessMutations'
import { resolveQuotaTenantId } from '@/hooks/access-control/resolveQuotaTenantId'
import { isPlatformUser } from '@/stores/auth'
import { useAuthStore } from '@/stores/authStore'
import { useProjectsStore } from '@/stores/projectsStore'
import { useQuotaStore } from '@/stores/quotaStore'
import { useSubscriptionStore } from '@/stores/subscriptionStore'
import { coalesceByKey } from '@/utils/coalesce'
import { isOwnerOfOrganization } from '@/utils/permissions'

const coalesce = coalesceByKey(400)

/**
 * Every refresh here needs an org, and both logout and the first moments of login leave the store
 * without one. Reading anyway fails with "Organization ID not available for fetching quota usage",
 * which the store keeps as its error and the billing page renders.
 */
const authContext = () => {
	const { currentTenant, currentOrganizationId, user, isAuthenticated } = useAuthStore.getState()
	if (!isAuthenticated || !user || !currentOrganizationId) {
		return null
	}
	return { currentTenant, currentOrganizationId, user }
}

const scheduleRefresh = (key: string, failure: string, run: () => Promise<unknown>): void => {
	coalesce(key, () => {
		run().catch(error => console.error(failure, error))
	})
}

/**
 * Refresh the tenant whose usage the gated CTAs on screen are reading, which is not necessarily the
 * tenant the mutation was addressed to: an org-level page deletes a user from some project while
 * its own CTA reads the root tenant's quota.
 */
export const scheduleActiveTenantQuotaRefresh = (): void => {
	const context = authContext()
	if (!context) {
		return
	}

	// The counters come from an owner-only endpoint. For everyone else `quotaApi` returns zero
	// consumption without a request, which can never move a gate but does re-render every consumer.
	if (!isOwnerOfOrganization(context.user, context.currentOrganizationId)) {
		return
	}

	const tenantId = resolveQuotaTenantId({
		currentTenant: context.currentTenant,
		pathname: window.location.pathname,
		rootTenantId: useProjectsStore.getState().rootTenantId,
		organizationId: context.currentOrganizationId,
		user: context.user,
	})

	if (tenantId) {
		useQuotaStore.getState().scheduleQuotaRefresh(tenantId)
	}
}

/**
 * `hasPermission` reads `user.role.permissions` out of the persisted auth user, otherwise refreshed
 * only on a tenant switch, so a role edit never reaches the CTA it gates.
 */
export const scheduleGrantsRefresh = (): void => {
	if (!authContext()) {
		return
	}

	scheduleRefresh('grants', 'Failed to refresh permissions after a grant change:', () =>
		useAuthStore.getState().refreshUser(),
	)
}

/**
 * `useCurrentPlan` reads billing status out of persisted storage, and only the billing surfaces
 * refresh it, so a plan change elsewhere leaves features locked for the rest of the session.
 */
export const scheduleBillingRefresh = (): void => {
	const context = authContext()
	if (!context) {
		return
	}

	scheduleRefresh('plan', 'Failed to refresh billing status after a plan change:', () =>
		useSubscriptionStore.getState().fetchBillingStatus(context.currentOrganizationId),
	)
}

const REFRESHERS: Record<AccessSurface, () => void> = {
	quota: scheduleActiveTenantQuotaRefresh,
	grants: scheduleGrantsRefresh,
	plan: scheduleBillingRefresh,
}

export const refreshAccessSurfaces = (surfaces: AccessSurface[]): void => {
	for (const surface of surfaces) {
		REFRESHERS[surface]?.()
	}
}

const ownsActiveOrg = (state: ReturnType<typeof useAuthStore.getState>): boolean =>
	isOwnerOfOrganization(state.user, state.currentOrganizationId ?? '')

const projectCount = (state: ReturnType<typeof useAuthStore.getState>): number => {
	const { user, currentOrganizationId } = state
	if (!user || !isPlatformUser(user) || !Array.isArray(user.tenant_list)) {
		return 0
	}
	return user.tenant_list.filter(tenant => tenant.org_id === currentOrganizationId).length
}

/**
 * The three changes no request reports.
 *
 * Projects consumption is the one quota counter not read from `/metrics`, so it settles only once
 * `refreshUser()` has landed a new tenant list. Billing status is cached per org, so an org switch
 * otherwise plan-gates the new org against the previous org's tier. Promotion to owner turns the
 * counters from zeroes into real numbers, and the cached zeroes would stand until the TTL expires.
 */
export const startAccessSync = (): (() => void) => {
	const stops = [
		useAuthStore.subscribe(projectCount, scheduleActiveTenantQuotaRefresh),
		useAuthStore.subscribe(state => state.currentOrganizationId, scheduleBillingRefresh),
		useAuthStore.subscribe(ownsActiveOrg, scheduleActiveTenantQuotaRefresh),
	]

	return () => {
		for (const stop of stops) {
			stop()
		}
	}
}
